'use strict'

// Form submission actions that write records. The designer stores this shape.
// The app server checks it again against the live spreadsheets before writing.

const FIELD_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/
const RECORD_ACTION_TYPES = ['set_related_value', 'create_related', 'create_record']
const SKIP_WRITE_TYPES = new Set(['password', 'image', 'file', 'formula', 'relationship', 'signature', 'sequence'])
const SKIP_COPY_TYPES = new Set(['password', 'image', 'file', 'relationship', 'signature', 'sequence'])
const SYSTEM_SOURCES = [
  { uniqueTitle: 'id', title: 'ID', type: 'number' },
  { uniqueTitle: 'createdAt', title: 'Created At', type: 'dateTime' },
  { uniqueTitle: 'updatedAt', title: 'Updated At', type: 'dateTime' }
]

function sheetByTitle (sheets, uniqueTitle) {

  return (sheets || []).find((sheet) => sheet && sheet.uniqueTitle === uniqueTitle) || null
}

function writableColumns (sheet) {

  return ((sheet && sheet.columns) || []).filter((column) => {
    return column && FIELD_NAME.test(column.uniqueTitle || '') && !SKIP_WRITE_TYPES.has(column.type)
  }).map((column) => ({
    uniqueTitle: column.uniqueTitle,
    title: column.title || column.uniqueTitle,
    type: column.type || 'text'
  }))
}

function assignmentColumns (sheet, userSpreadSheet) {

  const columns = writableColumns(sheet)

  if (!userSpreadSheet || !sheet) return columns

  for (const column of sheet.columns || []) {

    if (!column || column.type !== 'relationship' || column.relatedSpreadSheet !== userSpreadSheet) continue
    if (!FIELD_NAME.test(column.uniqueTitle || '')) continue
    if (columns.some((item) => item.uniqueTitle === column.uniqueTitle)) continue

    columns.push({
      uniqueTitle: column.uniqueTitle,
      title: column.title || column.uniqueTitle,
      type: 'relationship',
      loggedInUser: true
    })
  }

  return columns
}

function copyableSourceFields (sheet) {

  const fields = SYSTEM_SOURCES.map((field) => ({ ...field }))
  const seen = new Set(fields.map((field) => field.uniqueTitle))

  for (const column of (sheet && sheet.columns) || []) {

    if (!column || !column.uniqueTitle || seen.has(column.uniqueTitle)) continue
    if (SKIP_COPY_TYPES.has(column.type)) continue

    seen.add(column.uniqueTitle)
    fields.push({
      uniqueTitle: column.uniqueTitle,
      title: column.title || column.uniqueTitle,
      type: column.type || 'text'
    })
  }

  return fields
}

function relationshipWriteTargets (spreadsheet, allSpreadsheets) {

  if (!spreadsheet) return []

  const sheets = allSpreadsheets || []
  const targets = []
  const seen = new Set()

  function push (target) {

    if (!target.key || seen.has(target.key)) return
    if (!sheetByTitle(sheets, target.relatedSpreadSheet)) return

    seen.add(target.key)
    targets.push(target)
  }

  for (const column of spreadsheet.columns || []) {

    if (!column || column.type !== 'relationship' || !column.relatedSpreadSheet) continue
    if (!FIELD_NAME.test(column.uniqueTitle || '')) continue

    const related = sheetByTitle(sheets, column.relatedSpreadSheet)
    const via = column.title || column.uniqueTitle
    const relatedTitle = related ? (related.title || related.uniqueTitle) : column.relatedSpreadSheet

    push({
      key: `forward:${column.uniqueTitle}`,
      label: `${via} → ${relatedTitle}`,
      relatedSource: 'forward',
      relationshipColumn: column.uniqueTitle,
      sourceSpreadsheet: '',
      sourceRelationshipColumn: '',
      relatedSpreadSheet: column.relatedSpreadSheet
    })
  }

  for (const sheet of sheets) {

    if (!sheet || sheet.uniqueTitle === spreadsheet.uniqueTitle || !sheet.columns) continue

    for (const column of sheet.columns) {

      if (!column || column.type !== 'relationship' || column.relatedSpreadSheet !== spreadsheet.uniqueTitle) continue
      if (!FIELD_NAME.test(column.uniqueTitle || '') || !FIELD_NAME.test(sheet.uniqueTitle || '')) continue

      const owner = sheet.title || sheet.uniqueTitle
      const via = column.title || column.uniqueTitle

      push({
        key: `reverse:${sheet.uniqueTitle}:${column.uniqueTitle}`,
        label: `${owner} (${via})`,
        relatedSource: 'reverse',
        relationshipColumn: '',
        sourceSpreadsheet: sheet.uniqueTitle,
        sourceRelationshipColumn: column.uniqueTitle,
        relatedSpreadSheet: sheet.uniqueTitle
      })
    }
  }

  return targets
}

function findRelationshipTarget (spreadsheet, allSpreadsheets, action) {

  const targets = relationshipWriteTargets(spreadsheet, allSpreadsheets)
  const source = action && action.relatedSource === 'reverse' ? 'reverse' : 'forward'

  if (source === 'reverse') {

    return targets.find((target) => {
      return target.relatedSource === 'reverse'
        && target.sourceSpreadsheet === action.sourceSpreadsheet
        && target.sourceRelationshipColumn === action.sourceRelationshipColumn
    }) || null
  }

  return targets.find((target) => {
    return target.relatedSource === 'forward' && target.relationshipColumn === action.relationshipColumn
  }) || null
}

function cleanAssignment (assignment, targetSheet, sourceSheet, userSpreadSheet) {

  if (!assignment || typeof assignment !== 'object') return null

  const field = typeof assignment.field === 'string' ? assignment.field : ''

  if (assignment.valueSource === 'loggedInUser') {

    const column = ((targetSheet && targetSheet.columns) || []).find((item) => item && item.uniqueTitle === field)

    if (!column || column.type !== 'relationship' || column.relatedSpreadSheet !== userSpreadSheet) return null

    return { field, valueSource: 'loggedInUser' }
  }

  if (!writableColumns(targetSheet).some((column) => column.uniqueTitle === field)) return null

  if (assignment.valueSource === 'field') {

    const sourceField = typeof assignment.sourceField === 'string' ? assignment.sourceField : ''

    if (!copyableSourceFields(sourceSheet).some((column) => column.uniqueTitle === sourceField)) return null

    return { field, valueSource: 'field', sourceField }
  }

  return {
    field,
    valueSource: 'static',
    staticValue: assignment.staticValue == null ? '' : assignment.staticValue
  }
}

function relationshipFields (target) {

  return {
    relatedSource: target.relatedSource,
    relationshipColumn: target.relationshipColumn,
    sourceSpreadsheet: target.sourceSpreadsheet,
    sourceRelationshipColumn: target.sourceRelationshipColumn,
    relatedSpreadSheet: target.relatedSpreadSheet
  }
}

function normalizeRecordAction (action, spreadsheet, allSpreadsheets, userSpreadSheet) {

  if (!action || !RECORD_ACTION_TYPES.includes(action.type)) return null

  if (action.type === 'create_record') {

    const sheet = sheetByTitle(allSpreadsheets, action.spreadSheet)

    if (!sheet) return null

    const values = (action.values || []).map((item) => cleanAssignment(item, sheet, spreadsheet, userSpreadSheet)).filter(Boolean)

    return { type: 'create_record', spreadSheet: sheet.uniqueTitle, values }
  }

  const target = findRelationshipTarget(spreadsheet, allSpreadsheets, action)

  if (!target) return null

  const relatedSheet = sheetByTitle(allSpreadsheets, target.relatedSpreadSheet)

  if (action.type === 'set_related_value') {

    const assignment = cleanAssignment({
      field: action.field,
      valueSource: action.valueSource,
      staticValue: action.staticValue,
      sourceField: action.sourceField
    }, relatedSheet, spreadsheet, userSpreadSheet)

    if (!assignment) return null

    return Object.assign({ type: 'set_related_value' }, relationshipFields(target), assignment)
  }

  const values = (action.values || []).map((item) => cleanAssignment(item, relatedSheet, spreadsheet, userSpreadSheet)).filter(Boolean)

  return Object.assign({ type: 'create_related', values }, relationshipFields(target))
}

function assignmentValue (assignment, recordValues) {

  if (!assignment || assignment.valueSource === 'loggedInUser') return undefined

  if (assignment.valueSource === 'field') {

    const value = recordValues ? recordValues[assignment.sourceField] : undefined

    if (value === undefined || value === null) return undefined

    return value
  }

  return assignment.staticValue == null ? '' : assignment.staticValue
}

function valuesFromAssignments (assignments, recordValues) {

  const values = {}

  for (const assignment of assignments || []) {

    const value = assignmentValue(assignment, recordValues)

    if (value === undefined) continue

    values[assignment.field] = value
  }

  return values
}

module.exports = {
  RECORD_ACTION_TYPES,
  relationshipWriteTargets,
  writableColumns,
  assignmentColumns,
  copyableSourceFields,
  normalizeRecordAction,
  assignmentValue,
  valuesFromAssignments
}
